import React from 'react';
import './Experience.css';

const Experience = () => {
  const experiences = [
    {
      id: 1,
      company: "Distribution Management",
      position: "Software Developer",
      duration: "October 2024 - May 2025",
      location: "St. Charles, MO",
      description: "Enhanced existing features and implemented new functionality based on business needs and user feedback.",
      achievements: [
        "Enhanced existing features and implemented new functionality based on business needs and user feedback",
        "Worked closely with other developers and departments to troubleshoot and resolve technical issues",
        "Used Azure DevOps to manage work items, track progress, and collaborate with team members on user stories and tasks",
        "Wrote and optimized SQL queries and stored procedures to improve data access performance",
        "Participated in code cleanup efforts, removing outdated or unused logic to streamline the codebase",
        "Participated in cross-departmental meetings to gather requirements, discuss proposed features, and align development with business needs",
        "Leveraged AI tools responsibly for code troubleshooting while prioritizing independent problem-solving and data security"
      ],
      technologies: ["C#", "SQL", "Azure DevOps", "ASP.NET", "SQL Server", "AI-Assisted Debugging"]
    },
    {
      id: 2,
      company: "Ranken Technical College",
      position: "IT Services Web Developer",
      duration: "November 2023 - May 2024",
      location: "St. Louis, MO",
      description: "Update and maintain website plugins and check website pages for issues with hyperlinks, formatting, and spelling.",
      achievements: [
        "Update and maintain website plugins",
        "Check the website pages for issues with hyperlinks, formatting, and spelling",
        "Ensure website functionality and user experience"
      ],
      technologies: ["WordPress", "HTML", "CSS", "JavaScript", "Web Development"]
    },
    {
      id: 3,
      company: "Ranken Technical College",
      position: "Application & Web Development Tutor",
      duration: "August 2023 - May 2024",
      location: "St. Louis, MO",
      description: "Provide guidance on programming languages and frameworks while guiding students to use the best coding practices.",
      achievements: [
        "Provide guidance on programming languages and frameworks",
        "Guiding students to use the best coding practices",
        "Help students understand complex programming concepts"
      ],
      technologies: ["C#", "JavaScript", "React", "ASP.NET", "Teaching"]
    },
    {
      id: 4,
      company: "Ranken Technical College",
      position: "Student Success Student Representative",
      duration: "August 2023 - May 2024",
      location: "St. Louis, MO",
      description: "Assist students with accessing library computers and online classes while helping Student Success staff with various tasks.",
      achievements: [
        "Assist students with accessing the library computers",
        "Help students with accessing online classes and resources",
        "Assisting Student Success staff with a wide range of tasks"
      ],
      technologies: ["Customer Service", "Technical Support", "Student Services"]
    },
    {
      id: 5,
      company: "Buchheit\n(Farm/General Store)",
      position: "Assistant Customer Service Manager",
      duration: "June 2020 - June 2022",
      location: "Just outside St. Louis, MO",
      description: "Manage, train, and delegate daily tasks to team members while providing excellent customer service.",
      achievements: [
        "Manage, train, and delegate daily tasks to team members",
        "Provide excellent customer service",
        "Count money, manage deposits, and count register draws"
      ],
      technologies: ["Management", "Customer Service", "Training", "Financial Management"]
    },
    {
      id: 6,
      company: "Family Business Operations",
      position: "Customer Service & IT Support",
      duration: "2015 - Present",
      location: "House Springs, MO",
      description: "Worked in family laundromat business and assisted with father's IT consulting services, gaining unique insights into user experience and technical support.",
      achievements: [
        "Served diverse customer base of all ages and technical abilities at family laundromat",
        "Developed user-centric mindset by understanding needs of non-technical customers",
        "Assisted with IT consulting services including phone and hands-on technical troubleshooting",
        "Gained experience in hardware and software troubleshooting for various computer systems",
        "Learned to communicate technical solutions in accessible language for all users",
        "Applied customer service insights to create intuitive, accessible software solutions"
      ],
      technologies: ["Customer Service", "Technical Support", "Hardware Troubleshooting", "User Experience Design", "Communication"]
    }
  ];

  const education = [
    {
      degree: "Associate of Technology Degree in Application & Web Development",
      school: "Ranken Technical College",
      duration: "August 2022 - May 2024",
      location: "St. Louis, MO",
      description: "Focused on full-stack web development, programming languages, and modern development practices."
    }
  ];

  const certifications = [
    {
      name: "Application & Web Development",
      issuer: "Ranken Technical College",
      year: "2024",
      description: "Associate of Technology Degree in Application & Web Development with focus on full-stack development."
    }
  ];

  return (
    <div className="experience">
      <div className="container">
        <section className="experience-hero">
          <h1 className="section-title">Experience</h1>
          <p className="experience-intro">
            My professional journey in software development, including work experience, 
            education, and certifications that have shaped my career.
          </p>
        </section>

        <section className="work-experience">
          <h2 className="section-subtitle">Work Experience</h2>
          <div className="timeline">
            {experiences.map((exp, index) => (
              <div key={exp.id} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <div className="experience-header">
                    <h3 className="company-name">{exp.company}</h3>
                    <span className="duration">{exp.duration}</span>
                  </div>
                  <h4 className="position">{exp.position}</h4>
                  <p className="location">{exp.location}</p>
                  <p className="description">{exp.description}</p>
                  
                  <div className="achievements">
                    <h5>Key Achievements:</h5>
                    <ul>
                      {exp.achievements.map((achievement, idx) => (
                        <li key={idx}>{achievement}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="technologies">
                    <h5>Technologies Used:</h5>
                    <div className="tech-tags">
                      {exp.technologies.map(tech => (
                        <span key={tech} className="tech-tag">{tech}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="education-section">
          <h2 className="section-subtitle">Education</h2>
          <div className="education-grid">
            {education.map((edu, index) => (
              <div key={index} className="education-card">
                <div className="education-header">
                  <h3 className="degree">{edu.degree}</h3>
                  <span className="duration">{edu.duration}</span>
                </div>
                <h4 className="school">{edu.school}</h4>
                <p className="location">{edu.location}</p>
                <p className="description">{edu.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="certifications-section">
          <h2 className="section-subtitle">Certifications</h2>
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="certification-card">
                <div className="cert-header">
                  <h3 className="cert-name">{cert.name}</h3>
                  <span className="cert-year">{cert.year}</span>
                </div>
                <p className="cert-issuer">{cert.issuer}</p>
                <p className="cert-description">{cert.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="experience-cta">
          <div className="cta-content">
            <h2>Want to discuss what I could bring to your team?</h2>
            <p>Let's Talk!</p>
            <div className="contact-info">
              <div className="contact-item">
                <strong>Email:</strong>
                <a href="mailto:JacobW@segotw.com">JacobW@segotw.com</a>
              </div>
              <div className="contact-item">
                <strong>Phone:</strong>
                <a href="tel:+13149399335">+1 (314) 939-9335</a>
              </div>
            </div>
            <div className="cta-buttons">
              <a href="/projects" className="btn btn-primary">
                View My Projects
              </a>
              <a href="/about" className="btn btn-secondary">
                Learn More About Me
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Experience; 